const EMPTY_ARRAY = [];

function firstTruthy(...values) {
  return values.find(Boolean);
}

function normalizedText(values, fallback = '') {
  return String(firstTruthy(...values) ?? fallback).trim() || fallback;
}

function finiteNumber(primary, fallback = null) {
  return Number.isFinite(primary) ? primary : fallback;
}

function objectRecord(value) {
  return value && typeof value === 'object' ? value : {};
}

export const MaterialScope = Object.freeze({
  PROJECT: 'project',
  MESSAGE: 'message',
});

export const MaterialSource = Object.freeze({
  LOCAL_UPLOAD: 'local_upload',
  MCP: 'mcp',
  EXTERNAL_CONNECTOR: 'external_connector',
});

export const MaterialStatus = Object.freeze({
  UPLOADING: 'uploading',
  PROCESSING: 'processing',
  READY: 'ready',
  FAILED: 'failed',
  DUPLICATE: 'duplicate',
});

export function getSessionListItemId(session) {
  const record = objectRecord(session);
  return firstTruthy(record.sessionId, record.id) ?? '';
}

export function normalizeSourceItem(item, index = 0) {
  const record = objectRecord(item);
  const fallbackName = `资料 ${index + 1}`;
  const normalizedName = normalizedText([record.name, record.filename], fallbackName);
  const normalizedId = firstTruthy(
    record.id,
    record.uid,
    `source-${normalizedName.toLowerCase().replace(/\s+/g, '-')}-${index}`
  );
  const linkedSessionIds = Array.isArray(record.linkedSessionIds)
    ? Array.from(new Set(record.linkedSessionIds.filter(Boolean)))
    : EMPTY_ARRAY;

  return {
    id: normalizedId,
    linkedSessionIds,
    name: normalizedName,
    size: finiteNumber(record.size),
    type: record.type ?? '',
  };
}

export function normalizeMaterialRecord(item = {}, index = 0, defaults = {}) {
  const record = objectRecord(item);
  const fallback = objectRecord(defaults);
  const normalizedSource = normalizeSourceItem({
    id: firstTruthy(record.id, record.source_id, record.uid),
    name: firstTruthy(record.filename, record.file_name, record.name),
    size: finiteNumber(record.size, record.file_size),
    type: firstTruthy(record.mime_type, record.type),
    linkedSessionIds: record.linkedSessionIds,
  }, index);
  const scope = normalizedText([record.scope, fallback.scope], MaterialScope.PROJECT);
  const source = normalizedText([record.source, fallback.source], MaterialSource.LOCAL_UPLOAD);
  const status = normalizedText([record.status, fallback.status], MaterialStatus.READY);

  return {
    id: normalizedSource.id,
    project_id: firstTruthy(record.project_id, fallback.project_id) ?? null,
    message_id: firstTruthy(record.message_id, fallback.message_id) ?? null,
    scope,
    source,
    filename: normalizedSource.name,
    mime_type: normalizedSource.type,
    size: normalizedSource.size,
    created_at: firstTruthy(record.created_at, fallback.created_at, new Date().toISOString()),
    status,
    error_message: record.error_message ?? '',
    persisted_to_project: record.persisted_to_project === true,
    linkedSessionIds: normalizedSource.linkedSessionIds,
    name: normalizedSource.name,
    type: normalizedSource.type,
  };
}

export function resolveScopedProjectId(identityContext = {}, projectSlot = null) {
  const identity = objectRecord(identityContext);
  const project = objectRecord(projectSlot);
  return firstTruthy(identity.project_id, project.project_id, project.id) ?? null;
}

export function resolveScopedUserId(identityContext = {}) {
  const identity = objectRecord(identityContext);
  return firstTruthy(identity.user_id, identity.external_user_id) ?? null;
}

export function buildSessionScopeParams(identityContext = {}, projectSlot = null) {
  const projectId = resolveScopedProjectId(identityContext, projectSlot);
  const userId = resolveScopedUserId(identityContext);

  return {
    ...(projectId ? { project_id: projectId } : {}),
    ...(userId ? { user_id: userId } : {}),
  };
}

export function mergeProjectSourcesWithSessionFiles(
  prevSources = EMPTY_ARRAY,
  attachedSessionFiles = EMPTY_ARRAY,
  sessionId = null
) {
  const normalizedPrev = Array.isArray(prevSources) ? prevSources : EMPTY_ARRAY;
  if (!sessionId || !Array.isArray(attachedSessionFiles) || attachedSessionFiles.length === 0) {
    return normalizedPrev;
  }

  const nextItems = normalizedPrev.map((item) => ({
    ...item,
    linkedSessionIds: Array.isArray(item.linkedSessionIds)
      ? Array.from(new Set(item.linkedSessionIds.filter(Boolean)))
      : EMPTY_ARRAY,
  }));
  const signatureToIndex = new Map();

  nextItems.forEach((item, index) => {
    const signature = `${item.name}::${item.size || ''}::${item.type || ''}`;
    signatureToIndex.set(signature, index);
  });

  attachedSessionFiles.forEach((file, index) => {
    const normalizedItem = normalizeSourceItem({
      id: file.uid || `session-source-${sessionId}-${index}-${Date.now()}`,
      linkedSessionIds: [sessionId],
      name: file.name,
      size: file.size,
      type: file.type,
    }, index);
    const signature = `${normalizedItem.name}::${normalizedItem.size || ''}::${normalizedItem.type || ''}`;
    const existingIndex = signatureToIndex.get(signature);

    if (typeof existingIndex === 'number') {
      const existingItem = nextItems[existingIndex];
      nextItems[existingIndex] = {
        ...existingItem,
        linkedSessionIds: Array.from(new Set([...(existingItem.linkedSessionIds || EMPTY_ARRAY), sessionId])),
      };
      return;
    }

    signatureToIndex.set(signature, nextItems.length);
    nextItems.push(normalizedItem);
  });

  return nextItems;
}
